// Employee "My Products" — the personal marketplace of financial products the employer
// has enabled and the employee is eligible for. Discovery lives here; the full apply +
// consent flow lives in Loans & Advances (this page routes there to apply).
import React, { useEffect, useState } from 'react';
import { DataState } from '@/features/shared/components/ui/DataState';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Boxes, Building2, ArrowRight, Wallet, FileClock, Search, ShieldCheck, Smartphone } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useEntitlements } from '@/features/entitlements/useEntitlements';
import { Card, CardContent } from '@/features/shared/components/ui/card';
import { Button } from '@/features/shared/components/ui/button';
import { Badge } from '@/features/shared/components/ui/badge';
import { Skeleton } from '@/features/shared/components/ui/skeleton';
import { creditApi, type MyMarketplace, type MarketplaceOfferView } from '@/features/credit/api/creditApi';
import type { CreditApplication, CreditFacility } from '@/features/credit/types';
import { formatMoney } from '../payroll/lib/format';
import { StatCard } from '../payroll/components/StatCard';
import { productAction, categoryLabel } from './productAction';
import { KaziScoreCard } from './KaziScoreCard';
import { FinancialHealthStrip } from './FinancialHealthStrip';
import { getApiErrorMessage } from '@/lib/apiError';

// Reason keys from the discovery API -> human chips (explainable ranking).
const REASON_LABEL: Record<string, string> = {
  lowest_rate: 'Lowest cost',
  no_interest: 'No interest',
  employer_contribution: 'Employer contributes',
  band_discount: 'Your tier discount',
  high_approval_chance: 'High approval chance',
};

export default function MyProductsPage() {
  const navigate = useNavigate();
  const { t: tEveryday } = useTranslation('everydayServices');
  const { hasProduct } = useEntitlements();
  const hasCommerce = hasProduct('commerce');
  const [market, setMarket] = useState<MyMarketplace>({ categories: [], recommended: [] });
  const [q, setQ] = useState('');
  const [applications, setApplications] = useState<CreditApplication[]>([]);
  const [facilities, setFacilities] = useState<CreditFacility[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  // First-time policy acknowledgement gate (shown until the employee accepts).
  const [consentNeeded, setConsentNeeded] = useState(false);
  const [acking, setAcking] = useState(false);

  useEffect(() => {
    load();
  }, []);

  const load = React.useCallback(async () => {
    setLoading(true); setError(false);
    try {
      const [m, a, f, c] = await Promise.all([
        creditApi.myMarketplace(),
        creditApi.myApplications(),
        creditApi.myFacilities(),
        creditApi.getMarketplaceConsent().catch(() => ({ acknowledged: true, policy_version: '' })),
      ]);
      setMarket(m); setApplications(a); setFacilities(f);
      setConsentNeeded(!c.acknowledged);
    } catch (e) {
      // US-18: a load failure must NOT read as "no products available" — surface a
      // recoverable error with Retry instead of the empty state.
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  const acceptConsent = async () => {
    setAcking(true);
    try {
      await creditApi.acknowledgeMarketplaceConsent();
      setConsentNeeded(false);
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Could not save your acknowledgement'));
    } finally {
      setAcking(false);
    }
  };

  // Server-side search, debounced — the ranking (and its reasons) stay canonical.
  useEffect(() => {
    if (loading) return;
    const t = setTimeout(() => {
      creditApi.myMarketplace(q.trim() || undefined).then(setMarket).catch(() => {});
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  const offers = market.categories.flatMap((c) => c.offers);

  const activeCount = facilities.filter((f) => ['active', 'in_arrears', 'settling', 'in_recovery'].includes(f.status)).length;
  const pendingCount = applications.filter((a) => ['submitted', 'eligibility_passed', 'risk_review'].includes(a.status)).length;

  return (
    <div className="mx-auto max-w-6xl space-y-6 p-4 md:p-6">
      {/* First-time policy acknowledgement — shown before an employee views the products. */}
      {consentNeeded && !loading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-lg rounded-2xl bg-card p-6 shadow-xl">
            <div className="mb-3 flex items-center gap-3">
              <div className="rounded-xl bg-primary-500/10 p-2.5 text-primary-500"><ShieldCheck className="h-6 w-6" /></div>
              <h2 className="text-lg font-semibold text-foreground">Before you view your products</h2>
            </div>
            <div className="space-y-3 text-sm text-muted-foreground">
              <p>
                Your company has made these financial products available to you. KaziPeople helps you discover
                and apply for them — it is not the lender.
              </p>
              <ul className="list-disc space-y-1.5 pl-5">
                <li>Eligibility, terms, rates and approval are decided by the product provider (your employer and/or a partner lender), not by KaziPeople.</li>
                <li>Viewing a product is free and never an obligation — you're only bound if you apply and accept an agreement.</li>
                <li>If you apply, the details needed to assess your application are shared with the provider; you'll consent to that at the point of applying.</li>
                <li>Repayments for a product you take up may be deducted from your salary, per that product's agreement.</li>
              </ul>
              <p>By continuing, you acknowledge you've read and understood how these products work.</p>
            </div>
            <div className="mt-5 flex justify-end">
              <Button onClick={acceptConsent} disabled={acking}>
                {acking ? 'Saving…' : 'I understand — show my products'}
              </Button>
            </div>
          </div>
        </div>
      )}
      <header className="flex items-start gap-3">
        <div className="rounded-xl bg-primary-500/10 p-2.5 text-primary-500"><Boxes className="h-6 w-6" /></div>
        <div>
          <h1 className="text-xl font-semibold text-foreground">My Products</h1>
          <p className="mt-0.5 max-w-2xl text-sm text-muted-foreground">
            Financial products your company has made available to you. Choose one to see the terms and apply —
            you're never charged unless you take up a product.
          </p>
        </div>
      </header>

      {hasCommerce && (
        // Everyday Services lives with the employee's products; it keeps its own
        // route (and nav entry) because it does not depend on the credit product.
        <Card>
          <CardContent className="flex flex-col gap-3 p-5 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-start gap-3">
              <span className="rounded-xl bg-accent p-2 text-accent-foreground"><Smartphone className="h-5 w-5" aria-hidden="true" /></span>
              <div>
                <p className="font-semibold text-foreground">{tEveryday('myProducts.title')}</p>
                <p className="text-sm text-muted-foreground">{tEveryday('myProducts.description')}</p>
              </div>
            </div>
            <Button variant="outline" onClick={() => navigate('../everyday-services', { relative: 'path' })}>
              {tEveryday('myProducts.open')} <ArrowRight className="h-4 w-4" />
            </Button>
          </CardContent>
        </Card>
      )}

      <KaziScoreCard />
      <FinancialHealthStrip />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <StatCard icon={Boxes} label="Available" value={String(offers.length)} loading={loading} />
        <StatCard icon={Wallet} label="Active" value={String(activeCount)} loading={loading} />
        <StatCard icon={FileClock} label="In progress" value={String(pendingCount)} loading={loading} />
      </div>

      <div className="relative max-w-md">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search products, partners or categories…"
          aria-label="Search products"
          className="w-full rounded-lg border border-border bg-background py-2 pl-9 pr-3 text-sm text-foreground outline-none focus:border-orange-400"
        />
      </div>

      {!loading && !q && market.featured && (
        <section className="space-y-2">
          <div className="flex items-center gap-2">
            <h2 className="text-sm font-semibold text-foreground">{market.featured.title}</h2>
            <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Sponsored</span>
          </div>
          {market.featured.message && <p className="text-xs text-muted-foreground">{market.featured.message}</p>}
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <OfferCard o={market.featured.offer} navigate={navigate} />
          </div>
        </section>
      )}

      {!loading && !q && market.recommended.length > 0 && (
        <section className="space-y-3">
          <h2 className="text-sm font-semibold text-foreground">Recommended for you</h2>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {market.recommended.map((o) => <OfferCard key={`rec-${o.productId}`} o={o} navigate={navigate} />)}
          </div>
        </section>
      )}

      <section className="space-y-3">
        <h2 className="text-sm font-semibold text-foreground">{q ? 'Search results' : 'Available to you'}</h2>
        {loading ? (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {[0, 1, 2].map((i) => <Skeleton key={i} className="h-48 rounded-xl" />)}
          </div>
        ) : error ? (
          <Card><CardContent className="p-0">
            <DataState variant="error" onRetry={load} message="Couldn't load lending products." />
          </CardContent></Card>
        ) : offers.length === 0 ? (
          <Card><CardContent className="p-10 text-center text-sm text-muted-foreground">
            {q ? 'Nothing matches your search.' : 'No products are available for you yet. Your HR team enables products for the company.'}
          </CardContent></Card>
        ) : (
          <div className="space-y-6">
            {market.categories.map((c) => (
              <div key={c.key} className="space-y-3">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{categoryLabel(c.key)}</h3>
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {c.offers.map((o) => <OfferCard key={o.productId} o={o} navigate={navigate} />)}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>


      {(activeCount > 0 || pendingCount > 0) && (
        <Card>
          <CardContent className="flex items-center justify-between p-5">
            <div className="text-sm text-muted-foreground">
              You have <strong className="text-foreground">{activeCount}</strong> active and{' '}
              <strong className="text-foreground">{pendingCount}</strong> in progress.
            </div>
            <Button variant="outline" size="sm" className="gap-1" onClick={() => navigate('../loans')}>
              Manage in Loans &amp; Advances <ArrowRight className="h-4 w-4" />
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function OfferCard({ o, navigate }: { o: MarketplaceOfferView; navigate: ReturnType<typeof useNavigate> }) {
              const isPartner = !!o.partnerId;
              const isCredit = o.fulfillmentDomain === 'credit';
              const action = productAction(o);
              return (
                <Card key={o.productId} className="flex flex-col">
                  <CardContent className="flex flex-1 flex-col gap-3 p-5">
                    <div className="flex items-center justify-between">
                      <Badge variant="info" className="gap-1">
                        {isPartner && <Building2 className="h-3 w-3" />} {categoryLabel(o.category)}
                      </Badge>
                      {isCredit && (
                        <span className="text-xs text-muted-foreground">
                          {o.interestMethod === 'reducing' ? 'Reducing' : 'Flat'} ·{' '}
                          {o.effectiveRate != null ? (
                            <>
                              <s>{o.annualRate}%</s>{' '}
                              <span className="font-semibold text-emerald-600 dark:text-emerald-400">{o.effectiveRate}% p.a.</span>
                            </>
                          ) : (
                            <>{o.annualRate}% p.a.</>
                          )}
                        </span>
                      )}
                    </div>
                    <h3 className="text-base font-semibold text-foreground">{o.name}</h3>
                  {o.partnerName && (
                    <p className="-mt-2 text-xs text-muted-foreground">by {o.partnerName}</p>
                  )}
                  {o.reasons.filter((r) => REASON_LABEL[r]).length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                      {o.reasons.filter((r) => REASON_LABEL[r]).map((r) => (
                        <span key={r} className="rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-medium text-emerald-800 dark:bg-emerald-500/15 dark:text-emerald-400">
                          {REASON_LABEL[r]}
                        </span>
                      ))}
                      {o.active && (
                        <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground">Active</span>
                      )}
                    </div>
                  )}
                    {isCredit ? (
                      <dl className="grid grid-cols-2 gap-2 text-xs">
                        <div><dt className="text-muted-foreground">Amount</dt>
                          <dd className="font-medium text-foreground">{formatMoney(o.minAmount, o.currency)}–{formatMoney(o.maxAmount, o.currency)}</dd></div>
                        <div><dt className="text-muted-foreground">Max term</dt>
                          <dd className="font-medium text-foreground">{o.maxTermMonths} months</dd></div>
                      </dl>
                    ) : (
                      <div className="space-y-2 text-xs">
                        {!!o.employeePremium && o.employeePremium > 0 && (
                          <div className="flex items-baseline justify-between">
                            <span className="text-muted-foreground">Your premium</span>
                            <span className="font-semibold text-foreground">
                              {formatMoney(o.employeePremium, o.currency)}<span className="font-normal text-muted-foreground">/mo</span>
                            </span>
                          </div>
                        )}
                        {!!o.employerContribution && o.employerContribution > 0 && (
                          <div className="flex items-baseline justify-between">
                            <span className="text-muted-foreground">Employer contributes</span>
                            <span className="font-medium text-foreground">{formatMoney(o.employerContribution, o.currency)}/mo</span>
                          </div>
                        )}
                        <p className="leading-relaxed text-muted-foreground">
                          {o.coverage || 'Provided by a KaziPeople partner. Review coverage and enroll.'}
                        </p>
                      </div>
                    )}
                    <div className="mt-auto pt-2">
                      {o.eligible === false ? (
                        <div className="rounded-lg bg-muted px-3 py-2 text-center text-xs text-muted-foreground">
                          🔒 {o.eligibilityReason || 'Not available for your tier yet'} — grow your KaziScore to unlock.
                        </div>
                      ) : (
                        <Button size="sm" className="w-full gap-1" onClick={() => navigate(action.route)}>
                          View &amp; {action.label.toLowerCase()} <ArrowRight className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
}
